import type { AxiosInstance } from 'axios'
import type {
  FirmNewsFeedQuery,
  FirmNewsManageQuery,
  FirmNewsPublishPayload,
  FirmNewsUpsertPayload,
  IContentAsset,
  IFirmNews,
  IFactoryFunctionRequest,
  IFactoryResponse,
} from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'firmNews'

const normalizeDate = (value?: Date | string | null) => value ? new Date(value) : null

const normalizeContentAsset = (asset: IContentAsset): IContentAsset => ({
  ...asset,
  _created: new Date(asset._created),
  _updated: new Date(asset._updated),
  expiresAt: normalizeDate(asset.expiresAt),
  linkedAt: normalizeDate(asset.linkedAt),
  deletedAt: normalizeDate(asset.deletedAt),
})

const normalizeFirmNews = (newsItem: IFirmNews): IFirmNews => ({
  ...newsItem,
  _created: new Date(newsItem._created),
  _updated: new Date(newsItem._updated),
  publishedAt: normalizeDate(newsItem.publishedAt),
  scheduledAt: normalizeDate(newsItem.scheduledAt),
  expiresAt: normalizeDate(newsItem.expiresAt),
  pinnedAt: normalizeDate(newsItem.pinnedAt),
  deletedAt: normalizeDate(newsItem.deletedAt),
  assets: Array.isArray(newsItem.assets) ? newsItem.assets.map(normalizeContentAsset) : [],
})

const listFeed = (axios: IFactoryFunctionRequest) => (query: FirmNewsFeedQuery = {}): IFactoryResponse<IFirmNews[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    query,
    afterReceivingHook: (items: IFirmNews[]) => items.map(normalizeFirmNews),
  })
}

const listManage = (axios: IFactoryFunctionRequest) => (query: FirmNewsManageQuery = {}): IFactoryResponse<IFirmNews[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/manage`,
    query,
    afterReceivingHook: (items: IFirmNews[]) => items.map(normalizeFirmNews),
  })
}

const show = (axios: IFactoryFunctionRequest) => (newsId: string): IFactoryResponse<IFirmNews> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}`,
    afterReceivingHook: normalizeFirmNews,
  })
}

const create = (axios: IFactoryFunctionRequest) => (data: FirmNewsUpsertPayload): IFactoryResponse<IFirmNews, FirmNewsUpsertPayload> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data,
    afterReceivingHook: normalizeFirmNews,
  })
}

const update = (axios: IFactoryFunctionRequest) => (newsId: string, data: FirmNewsUpsertPayload): IFactoryResponse<IFirmNews, FirmNewsUpsertPayload> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}`,
    data,
    afterReceivingHook: normalizeFirmNews,
  })
}

const publish = (axios: IFactoryFunctionRequest) => (newsId: string, data: FirmNewsPublishPayload = {}): IFactoryResponse<IFirmNews, FirmNewsPublishPayload> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}/publish`,
    data,
    afterReceivingHook: normalizeFirmNews,
  })
}

const archive = (axios: IFactoryFunctionRequest) => (newsId: string): IFactoryResponse<IFirmNews> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}/archive`,
    afterReceivingHook: normalizeFirmNews,
  })
}

const pin = (axios: IFactoryFunctionRequest) => (newsId: string): IFactoryResponse<IFirmNews> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}/pin`,
    afterReceivingHook: normalizeFirmNews,
  })
}

const unpin = (axios: IFactoryFunctionRequest) => (newsId: string): IFactoryResponse<IFirmNews> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}/unpin`,
    afterReceivingHook: normalizeFirmNews,
  })
}

const remove = (axios: IFactoryFunctionRequest) => (newsId: string): IFactoryResponse<{ _id: string; deleted: boolean }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${newsId}`,
  })
}

export default (axios: AxiosInstance) => ({
  listFeed: listFeed(useAxios(axios)),
  listManage: listManage(useAxios(axios)),
  show: show(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  publish: publish(useAxios(axios)),
  archive: archive(useAxios(axios)),
  pin: pin(useAxios(axios)),
  unpin: unpin(useAxios(axios)),
  remove: remove(useAxios(axios)),
})
